import './utils/i18n';
import 'normalize.css';

import React, { Suspense } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';

import { Auth, useAuth } from '@kp/react-sdk';
import {
  AppInsightsErrorBoundary,
  initAppInsights,
} from '@kp/react-sdk/app-insights';
import { Toaster, injectGlobal } from '@kp/react-ui';
import { configureClient } from '@kp/rest-api-javascript-sdk';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ReactDOM from 'react-dom/client';

import App from './App';
import { ErrorFallback } from './components/Errors';
import { Loader } from './components/Loader';
import { AiChatProvider } from './contexts/ai-chat-provider';
import { GenericPageProvider } from './contexts/generic-page-context';
import { GraphQLProvider, setAuthToken } from './contexts/graphql-context';
import { SettingsProvider } from './contexts/settings-provider';
import {
  APPLICATIONINSIGHTS_CONNECTION_STRING,
  AUTH_URI,
  CLIENT_ID,
  REST_URI,
} from './utils/env';

injectGlobal({
  html: {
    boxSizing: 'border-box',
  },
  '*, *::before, *::after': {
    boxSizing: 'inherit',
  },
  ul: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  ol: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
});

if (APPLICATIONINSIGHTS_CONNECTION_STRING) {
  initAppInsights('hub-app', {
    connectionString: APPLICATIONINSIGHTS_CONNECTION_STRING,
    correlationHeaderExcludedDomains: ['id.*'],
  });
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

let authToken = '';

configureClient({ baseUrl: REST_URI, getToken: () => authToken });

const TokenSync = () => {
  const { user } = useAuth();
  authToken = user?.access_token || '';
  setAuthToken(authToken);
  return null;
};

root.render(
  <Suspense fallback={<Loader />}>
    <AppInsightsErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={console.warn}
    >
      <Router>
        <Auth
          clientId={CLIENT_ID}
          url={AUTH_URI}
          forbiddenUrl="/forbidden"
          LoadingComponent={Loader}
        >
          <TokenSync />
          <React.StrictMode>
            <QueryClientProvider client={queryClient}>
              <SettingsProvider>
                <GraphQLProvider>
                  <AiChatProvider>
                    <GenericPageProvider>
                      <App />
                      <Toaster />
                    </GenericPageProvider>
                  </AiChatProvider>
                </GraphQLProvider>
              </SettingsProvider>
            </QueryClientProvider>
          </React.StrictMode>
        </Auth>
      </Router>
    </AppInsightsErrorBoundary>
  </Suspense>,
);
